import { cn } from '@/shared/lib/cn';
import { Skeleton } from '@/shared/ui/skeleton';

/** Width presets that make skeleton text look natural instead of uniform bars. */
const WIDTHS = ['w-11/12', 'w-4/5', 'w-full', 'w-3/4', 'w-5/6', 'w-2/3'];

export function TextSkeleton({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-3', i === lines - 1 ? 'w-1/2' : WIDTHS[i % WIDTHS.length])} />
      ))}
    </div>
  );
}

export function ListSkeleton({
  rows = 5,
  avatar = true,
  className,
}: {
  rows?: number;
  avatar?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('divide-y', className)} aria-busy aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          {avatar && <Skeleton className="size-9 shrink-0 rounded-full" />}
          <div className="flex-1 space-y-2">
            <Skeleton className={cn('h-3', i % 2 ? 'w-1/3' : 'w-2/5')} />
            <Skeleton className={cn('h-2.5', i % 3 ? 'w-3/5' : 'w-1/2')} />
          </div>
          <Skeleton className="h-3 w-12" />
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('rounded-xl border bg-card p-5', className)} aria-hidden>
      <div className="flex items-center gap-3">
        <Skeleton className="size-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-2.5 w-1/4" />
        </div>
      </div>
      <TextSkeleton lines={3} className="mt-5" />
    </div>
  );
}

export function TableSkeleton({
  rows = 6,
  columns = 4,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) {
  return (
    <div className={cn('overflow-hidden rounded-xl border', className)} aria-hidden>
      <div className="flex gap-4 border-b bg-muted/40 px-3 py-3">
        {Array.from({ length: columns }, (_, c) => (
          <Skeleton key={c} className={cn('h-2.5', c === 0 ? 'w-1/4' : 'w-1/6')} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 border-b px-3 py-3.5 last:border-0">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className={cn('h-3', c === 0 ? 'w-1/4' : (r + c) % 2 ? 'w-1/5' : 'w-1/6')} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Placeholder for a whole page (header + content cards) while route data loads. */
export function PageSkeleton() {
  return (
    <div className="space-y-6" aria-busy aria-hidden>
      <div className="space-y-2">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-3 w-80 max-w-full" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <CardSkeleton />
        <CardSkeleton />
      </div>
      <ListSkeleton rows={4} />
    </div>
  );
}
